import { LoadingState } from '../../../shared/ui/LoadingState.jsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { bossDisplayName } from '../../../shared/domain/bossPresentation.js';
import { formatRaidHpEok, formatRaidHpInput, raidHpValueFromInput } from '../../../shared/domain/raidHpFormat.js';
import { ElementIcon } from '../../nikke-catalog/shared/ElementIcon.jsx';
import { clearUnmatchedPlanItems, mergeStrategyDrafts, strategyPlanSignature } from '../domain/raidStrategyDrafts.js';
import {
  plansWithoutCompletedTargets,
  summarizeRaidStrategyProgress,
} from '../domain/raidStrategyProgress.js';
import { strategyPlanTargets, strategyPlanUsage } from '../domain/raidStrategyRecommendation.js';
import { findBossTetrisCombinations } from '../domain/raidTetrisEngine.js';
import {
  canonicalStrategyDeck,
  firstStrategyIntegrityMessage,
  reconcileRaidStrategy,
  strategyActualReviewState,
  strategyIntegrityIssues,
  strategyMemberOpenid,
} from '../domain/raidStrategyReconciliation.js';
import { useRaidStrategyPlans } from '../hooks/useRaidStrategyPlans.js';
import { RaidStrategyPlacementDialog } from './RaidStrategyPlacementDialog.jsx';
import { RaidStrategyRecommendDialog } from './RaidStrategyRecommendDialog.jsx';
import { RaidStrategySlotDialog } from './RaidStrategySlotDialog.jsx';
import { RaidTetrisMiniDeck } from './RaidTetrisMiniDeck.jsx';
import { siteImageUrl } from '../../../shared/domain/siteImages.js';

const MIN_SLOT_ROWS = 4;
const MAX_SLOT_ROWS = 12;

function numberValue(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function textValue(value) {
  return String(value ?? '').trim();
}

function formatEok(value) {
  return `${numberValue(value).toLocaleString('ko-KR', { maximumFractionDigits: 3 })}억`;
}

function phaseModel(boss, phaseNo) {
  return (Array.isArray(boss?.phases) ? boss.phases : []).find((phase) => Number(phase.phaseNo) === Number(phaseNo)) || null;
}

function phaseResetTargetHp(boss, phaseNo) {
  const phase = phaseModel(boss, phaseNo);
  const maximum = Number(phase?.maxHpEok);
  if (Number.isFinite(maximum)) return Math.max(0, maximum);
  const remaining = Number(phase?.remainingHpEok);
  return Number.isFinite(remaining) ? Math.max(0, remaining) : 0;
}

function planKey(bossNo, phaseNo) {
  return `${Number(bossNo || 0)}:${Number(phaseNo || 0)}`;
}

function emptyPlan(boss, phaseNo) {
  return {
    id: '',
    bossNo: Number(boss?.bossNo || 1),
    phaseNo: Number(phaseNo || 1),
    targetHpEok: phaseResetTargetHp(boss, phaseNo),
    note: '',
    items: [],
    updatedAt: null,
  };
}

function clonePlan(plan) {
  return {
    ...plan,
    items: (Array.isArray(plan?.items) ? plan.items : []).map((item) => ({
      ...item,
      deck: Array.isArray(item.deck) ? [...item.deck] : [],
    })),
  };
}

function recommendationItems(recommendation = {}) {
  return recommendation?.selected?.items
    || recommendation?.results?.[0]?.items
    || recommendation?.insufficient?.items
    || [];
}

function candidateToPlanItem(candidate) {
  return {
    practiceId: textValue(candidate?.id),
    memberOpenid: textValue(candidate?.memberOpenid),
    nickname: textValue(candidate?.nickname) || '닉네임 확인 불가',
    bossNo: numberValue(candidate?.bossNo),
    slotNo: numberValue(candidate?.slotNo, 1),
    title: textValue(candidate?.title) || `모의전 ${numberValue(candidate?.slotNo, 1)}`,
    damageEok: numberValue(candidate?.damageEok),
    deck: Array.isArray(candidate?.deck) ? candidate.deck : [],
    note: '',
  };
}

function itemTypeLabel(item = {}) {
  return textValue(item.practiceId).startsWith('manual:')
    ? '직접 입력'
    : (textValue(item.title) || `모의전 슬롯 ${numberValue(item.slotNo, 1)}`);
}

function allPlansFromDrafts(bosses, drafts) {
  const output = [];
  for (const boss of bosses) {
    for (const phaseNo of [1, 2, 3]) {
      output.push(drafts[planKey(boss.bossNo, phaseNo)] || emptyPlan(boss, phaseNo));
    }
  }
  return output;
}

function draftItemsFromPlans(bosses, plans) {
  const output = [];
  for (const plan of Array.isArray(plans) ? plans : []) {
    const bossNo = numberValue(plan?.bossNo);
    const phaseNo = numberValue(plan?.phaseNo);
    const boss = bosses.find((item) => Number(item.bossNo) === bossNo);
    (Array.isArray(plan?.items) ? plan.items : []).forEach((item, index) => output.push({
      ...item,
      locationKey: planKey(bossNo, phaseNo),
      locationLabel: `${phaseNo}단 ${bossDisplayName(boss, bossNo)} 슬롯 ${index + 1}`,
    }));
  }
  return output;
}

function integrityIssueKey(issue = {}) {
  return [
    textValue(issue.type),
    textValue(issue.memberOpenid),
    textValue(issue.code),
  ].join('::');
}

export function RaidStrategyBoard({
  season,
  candidates = [],
  placementCandidates = [],
  viewerRole = '',
  mobile = false,
  embedded = false,
  recommendMode = 'nearest',
  onRecommendModeChange = () => {},
  preferAvailability = false,
  onPreferAvailabilityChange = () => {},
  currentAvailabilityOnly = false,
  onCurrentAvailabilityOnlyChange = () => {},
}) {
  const strategy = useRaidStrategyPlans(season?.seasonNo, { viewerRole });
  const bosses = Array.isArray(season?.bosses) ? season.bosses : [];
  const members = Array.isArray(season?.members) ? season.members : [];
  const actualRecords = Array.isArray(season?.records) ? season.records : [];
  const initialPhase = Math.max(1, Math.min(3, numberValue(season?.globalPhase, 1)));
  const [selectedPhaseNo, setSelectedPhaseNo] = useState(initialPhase);
  const [drafts, setDrafts] = useState({});
  const [dialog, setDialog] = useState(null);
  const [recommendDialog, setRecommendDialog] = useState(null);
  const [placementDialog, setPlacementDialog] = useState(null);
  const [pendingClear, setPendingClear] = useState(false);
  // '실전 반영' shows the plan as the raid actually went; '모의전만' shows the registered practice
  // plan itself, which is what a leader wants while rebuilding a phase.
  const [practiceOnly, setPracticeOnly] = useState(false);
  const [notice, setNotice] = useState({ type: '', message: '' });
  const [conflictPhases, setConflictPhases] = useState([]);
  const draftsRef = useRef(drafts);
  const savedSignaturesRef = useRef(new Map());
  const savedSeasonNoRef = useRef(season?.seasonNo);

  draftsRef.current = drafts;

  useEffect(() => {
    const incoming = {};
    for (const boss of bosses) {
      for (const phaseNo of [1, 2, 3]) {
        const key = planKey(boss.bossNo, phaseNo);
        const stored = strategy.plansByKey.get(key);
        incoming[key] = stored ? clonePlan(stored) : emptyPlan(boss, phaseNo);
      }
    }

    const seasonChanged = savedSeasonNoRef.current !== season?.seasonNo;
    savedSeasonNoRef.current = season?.seasonNo;

    const merged = mergeStrategyDrafts({
      drafts: seasonChanged ? {} : draftsRef.current,
      incoming,
      savedSignatures: seasonChanged ? new Map() : savedSignaturesRef.current,
    });
    savedSignaturesRef.current = merged.savedSignatures;
    draftsRef.current = merged.drafts;
    setDrafts(merged.drafts);
    setConflictPhases([...new Set(merged.conflicts.map((key) => numberValue(key.split(':')[1])))].sort());

  }, [bosses, season?.seasonNo, strategy.plansByKey]);

  useEffect(() => {
    setSelectedPhaseNo(Math.max(1, Math.min(3, numberValue(season?.globalPhase, 1))));
    setDialog(null);
    setNotice({ type: '', message: '' });
  }, [season?.globalPhase, season?.seasonNo]);

  const allPlans = useMemo(() => allPlansFromDrafts(bosses, drafts), [bosses, drafts]);
  const activeAllPlans = useMemo(() => plansWithoutCompletedTargets({
    plans: allPlans,
    actualRecords,
    reconcile: reconcileRaidStrategy,
  }), [actualRecords, allPlans]);
  const reconciliation = useMemo(() => strategyIntegrityIssues({ plans: activeAllPlans, actualRecords }), [activeAllPlans, actualRecords]);
  const phaseReconciliationByBoss = useMemo(() => new Map(bosses.map((boss) => {
    const plan = drafts[planKey(boss.bossNo, selectedPhaseNo)] || emptyPlan(boss, selectedPhaseNo);
    const actuals = actualRecords.filter((record) => Number(record?.bossNo) === Number(boss.bossNo)
      && Number(record?.phaseNo) === Number(selectedPhaseNo));
    return [Number(boss.bossNo), reconcileRaidStrategy({ plans: [plan], actualRecords: actuals })];
  })), [actualRecords, bosses, drafts, selectedPhaseNo]);

  const phaseProgressByBoss = useMemo(() => new Map(bosses.map((boss) => {
    const plan = drafts[planKey(boss.bossNo, selectedPhaseNo)] || emptyPlan(boss, selectedPhaseNo);
    return [Number(boss.bossNo), summarizeRaidStrategyProgress({
      reconciliation: phaseReconciliationByBoss.get(Number(boss.bossNo)),
      targetHpEok: plan.targetHpEok,
    })];
  })), [bosses, drafts, phaseReconciliationByBoss, selectedPhaseNo]);

  // 모의전 보기: a real attack takes over the plan slot it matched, so the planned lineup disappears
  // as soon as the union starts attacking. This recomputes the same maps as if no attack had landed,
  // for display only — saving, warnings and the boss target still run on the real reconciliation.
  const displayReconciliationByBoss = useMemo(() => (practiceOnly
    ? new Map(bosses.map((boss) => {
      const plan = drafts[planKey(boss.bossNo, selectedPhaseNo)] || emptyPlan(boss, selectedPhaseNo);
      return [Number(boss.bossNo), reconcileRaidStrategy({ plans: [plan], actualRecords: [] })];
    }))
    : phaseReconciliationByBoss), [bosses, drafts, phaseReconciliationByBoss, practiceOnly, selectedPhaseNo]);

  const displayProgressByBoss = useMemo(() => (practiceOnly
    ? new Map(bosses.map((boss) => {
      const plan = drafts[planKey(boss.bossNo, selectedPhaseNo)] || emptyPlan(boss, selectedPhaseNo);
      return [Number(boss.bossNo), summarizeRaidStrategyProgress({
        reconciliation: displayReconciliationByBoss.get(Number(boss.bossNo)),
        targetHpEok: plan.targetHpEok,
      })];
    }))
    : phaseProgressByBoss), [bosses, displayReconciliationByBoss, drafts, phaseProgressByBoss, practiceOnly, selectedPhaseNo]);

  // Saving is per phase, but drafts exist for all three — a recommendation can fill 1~3단 at once —
  // so every phase needs its own save payload and dirty flag, not just the visible one.
  const plansForSaveByPhase = useMemo(() => new Map([1, 2, 3].map((phaseNo) => [phaseNo, bosses.map((boss) => {
    const plan = drafts[planKey(boss.bossNo, phaseNo)] || emptyPlan(boss, phaseNo);
    const progress = summarizeRaidStrategyProgress({
      reconciliation: reconcileRaidStrategy({
        plans: [plan],
        actualRecords: actualRecords.filter((record) => Number(record?.bossNo) === Number(boss.bossNo)
          && Number(record?.phaseNo) === Number(phaseNo)),
      }),
      targetHpEok: plan.targetHpEok,
    });
    return progress.targetReached && plan.items.length ? { ...plan, items: [] } : plan;
  })])), [actualRecords, bosses, drafts]);

  const dirtyPhases = useMemo(() => [1, 2, 3].filter((phaseNo) => (plansForSaveByPhase.get(phaseNo) || []).some((plan) => {
    const boss = bosses.find((entry) => Number(entry.bossNo) === Number(plan.bossNo));
    const saved = strategy.plansByKey.get(planKey(plan.bossNo, phaseNo)) || emptyPlan(boss, phaseNo);
    return strategyPlanSignature(plan) !== strategyPlanSignature(saved);
  })), [bosses, plansForSaveByPhase, strategy.plansByKey]);

  const allDraftItems = useMemo(() => draftItemsFromPlans(bosses, allPlans), [allPlans, bosses]);
  const activeDraftItems = useMemo(() => draftItemsFromPlans(bosses, activeAllPlans), [activeAllPlans, bosses]);

  const assignedLocationById = useMemo(() => new Map(activeDraftItems
    .filter((item) => item.practiceId)
    .map((item) => [item.practiceId, item.locationLabel])), [activeDraftItems]);

  const candidatesByBoss = useMemo(() => {
    const map = new Map();
    for (const candidate of candidates) {
      const bossNo = numberValue(candidate?.bossNo);
      const list = map.get(bossNo) || [];
      list.push(candidate);
      map.set(bossNo, list);
    }
    return map;
  }, [candidates]);

  const activeAssignedIds = useMemo(() => new Set(activeDraftItems.map((item) => item.practiceId).filter(Boolean)), [activeDraftItems]);
  const planUsage = useMemo(() => strategyPlanUsage(reconciliation.effectiveAttacks), [reconciliation]);
  const unassignedCandidates = useMemo(() => candidates
    .filter((candidate) => !activeAssignedIds.has(candidate?.id)), [activeAssignedIds, candidates]);
  const syncLevelByOpenid = useMemo(() => new Map(members
    .map((member) => [textValue(member.memberOpenid ?? member.openid), numberValue(member.syncLevel ?? member.synchroLevel ?? member.level)])
    .filter(([openid, level]) => openid && level > 0)), [members]);

  const phaseTargetsByBoss = useMemo(() => new Map(strategyPlanTargets({
    bosses,
    plans: activeAllPlans,
    actualRecords,
    phases: [selectedPhaseNo],
  }).map((target) => [target.bossNo, target])), [actualRecords, activeAllPlans, bosses, selectedPhaseNo]);

  // Only the boss whose slot dialog is open needs a recommendation here — it marks the recommended
  // candidates in that list. The recommend dialog does its own, wider search.
  const dialogRecommendation = useMemo(() => {
    const target = dialog ? phaseTargetsByBoss.get(Number(dialog.bossNo)) : null;
    if (!target || target.remaining <= 0) return null;
    return findBossTetrisCombinations({
      candidates: unassignedCandidates,
      bossNo: Number(dialog.bossNo),
      targetHpEok: target.remaining,
      mode: recommendMode,
      preferAvailability,
      memberUsage: planUsage.memberUsage,
      memberUsedCodes: planUsage.memberUsedCodes,
      topN: 3,
    });
  }, [dialog, phaseTargetsByBoss, planUsage, preferAvailability, recommendMode, unassignedCandidates]);

  const phaseDirty = dirtyPhases.includes(selectedPhaseNo);

  const setBossPlan = (bossNo, updater) => {
    const boss = bosses.find((item) => Number(item.bossNo) === Number(bossNo));
    const key = planKey(bossNo, selectedPhaseNo);
    setDrafts((current) => {
      const previous = current[key] || emptyPlan(boss, selectedPhaseNo);
      const next = typeof updater === 'function' ? updater(previous) : updater;
      return { ...current, [key]: next };
    });
  };

  const draftsWithoutDialogItem = () => {
    const nextDrafts = { ...drafts };
    if (!dialog) return nextDrafts;
    const key = planKey(dialog.bossNo, selectedPhaseNo);
    const boss = bosses.find((entry) => Number(entry.bossNo) === Number(dialog.bossNo));
    const plan = clonePlan(nextDrafts[key] || emptyPlan(boss, selectedPhaseNo));
    if (dialog.planIndex < plan.items.length) plan.items.splice(dialog.planIndex, 1);
    nextDrafts[key] = plan;
    return nextDrafts;
  };

  const draftsWithDialogAssignment = (item) => {
    const nextDrafts = draftsWithoutDialogItem();
    if (!dialog) return nextDrafts;
    const key = planKey(dialog.bossNo, selectedPhaseNo);
    const boss = bosses.find((entry) => Number(entry.bossNo) === Number(dialog.bossNo));
    const plan = clonePlan(nextDrafts[key] || emptyPlan(boss, selectedPhaseNo));
    const normalized = { ...item, bossNo: Number(dialog.bossNo) };
    if (dialog.planIndex <= plan.items.length) plan.items.splice(dialog.planIndex, 0, normalized);
    else plan.items.push(normalized);
    nextDrafts[key] = plan;
    return nextDrafts;
  };

  const validateDrafts = (nextDrafts) => strategyIntegrityIssues({
    plans: plansWithoutCompletedTargets({
      plans: allPlansFromDrafts(bosses, nextDrafts),
      actualRecords,
      reconcile: reconcileRaidStrategy,
    }),
    actualRecords,
  });

  const assignmentIntegrityCheck = (item, currentItem = null) => {
    if (!item?.practiceId || !item?.memberOpenid) return { message: '슬롯 정보를 확인해주세요.', issues: [] };
    const assigned = activeDraftItems.find((entry) => entry.practiceId === item.practiceId && entry.practiceId !== currentItem?.practiceId);
    if (assigned) return { message: `이미 ${assigned.locationLabel}에 배치되어 있습니다.`, issues: [] };
    if (!dialog) return { message: '', issues: [] };

    const baseline = validateDrafts(draftsWithoutDialogItem());
    const baselineIssueKeys = new Set((baseline.issues || []).map(integrityIssueKey));
    const proposed = validateDrafts(draftsWithDialogAssignment(item));
    const newIssues = (proposed.issues || []).filter((issue) => !baselineIssueKeys.has(integrityIssueKey(issue)));
    return { message: firstStrategyIntegrityMessage({ ...proposed, issues: newIssues }), issues: newIssues };
  };

  const assignmentIntegrityReason = (item, currentItem = null) => assignmentIntegrityCheck(item, currentItem).message;

  const openSlot = (bossNo, displayRowIndex) => {
    const reconciled = displayReconciliationByBoss.get(Number(bossNo));
    const progress = displayProgressByBoss.get(Number(bossNo));
    const unmatchedPlans = reconciled?.unmatchedPlans || [];
    const displayed = unmatchedPlans[displayRowIndex] || null;
    if (progress?.targetReached && !displayed) {
      setNotice({ type: 'error', message: '이미 처치된 보스에는 계획 슬롯을 추가할 수 없습니다.' });
      return;
    }
    if (displayRowIndex > unmatchedPlans.length) {
      setNotice({ type: 'error', message: '위쪽 빈 슬롯부터 먼저 선택해주세요.' });
      return;
    }
    if (!strategy.canManage && !displayed) return;
    const plan = drafts[planKey(bossNo, selectedPhaseNo)] || emptyPlan(
      bosses.find((boss) => Number(boss.bossNo) === Number(bossNo)),
      selectedPhaseNo,
    );
    setDialog({
      bossNo: Number(bossNo),
      displayRowIndex: Number(displayRowIndex),
      planIndex: displayed?.planIndex ?? plan.items.length,
    });
    setNotice({ type: '', message: '' });
  };

  const applyDialogItem = (item) => {
    if (!dialog) return '';
    if (phaseProgressByBoss.get(Number(dialog.bossNo))?.targetReached) {
      const message = '이미 처치된 보스에는 계획 슬롯을 배치할 수 없습니다.';
      setNotice({ type: 'error', message });
      return message;
    }
    const key = planKey(dialog.bossNo, selectedPhaseNo);
    const currentPlan = drafts[key] || emptyPlan(
      bosses.find((boss) => Number(boss.bossNo) === dialog.bossNo),
      selectedPhaseNo,
    );
    const currentItem = currentPlan.items[dialog.planIndex] || null;
    const error = assignmentIntegrityReason(item, currentItem);
    if (error) {
      setNotice({ type: 'error', message: error });
      return error;
    }
    setBossPlan(dialog.bossNo, (plan) => {
      const items = [...plan.items];
      const normalized = { ...item, bossNo: dialog.bossNo };
      if (dialog.planIndex < items.length) items[dialog.planIndex] = normalized;
      else items.push(normalized);
      return { ...plan, items };
    });
    setDialog(null);
    setNotice({ type: 'success', message: `${item.nickname} 슬롯을 배치했습니다.` });
    return '';
  };

  const deleteDialogItem = () => {
    if (!dialog) return;
    setBossPlan(dialog.bossNo, (plan) => ({
      ...plan,
      items: plan.items.filter((_, index) => index !== dialog.planIndex),
    }));
    setDialog(null);
    setNotice({ type: 'success', message: '슬롯을 비웠습니다.' });
  };

  const activePlansFromDrafts = (nextDrafts) => plansWithoutCompletedTargets({
    plans: allPlansFromDrafts(bosses, nextDrafts),
    actualRecords,
    reconcile: reconcileRaidStrategy,
  });

  const usedPracticeIdsFromDrafts = (nextDrafts) => new Set(
    draftItemsFromPlans(bosses, activePlansFromDrafts(nextDrafts))
      .map((item) => item.practiceId).filter(Boolean),
  );

  // Puts a chosen recommendation into the draft, slot by slot. A slot that would break the 3-attack
  // or Nikke-reuse rule is skipped and reported instead of quietly landing in the plan.
  const addItemsToDrafts = (nextDraftsIn, bossNo, phaseNo, items) => {
    const boss = bosses.find((entry) => Number(entry.bossNo) === Number(bossNo));
    const key = planKey(bossNo, phaseNo);
    let workingDrafts = { ...nextDraftsIn, [key]: clonePlan(nextDraftsIn[key] || emptyPlan(boss, phaseNo)) };
    const usedPracticeIds = usedPracticeIdsFromDrafts(workingDrafts);
    let added = 0;

    for (const item of Array.isArray(items) ? items : []) {
      if (!item?.id || usedPracticeIds.has(item.id)) continue;
      const plan = clonePlan(workingDrafts[key]);
      plan.items.push(candidateToPlanItem(item));
      const proposed = { ...workingDrafts, [key]: plan };
      if (validateDrafts(proposed).issues.length) continue;
      workingDrafts = proposed;
      usedPracticeIds.add(item.id);
      added += 1;
    }
    return { nextDrafts: added ? workingDrafts : nextDraftsIn, added };
  };

  const applyRecommendation = (entries) => {
    if (!strategy.canManage) return '운영진만 계획을 편집할 수 있습니다.';
    const requested = entries.reduce((sum, entry) => sum + (entry.items?.length || 0), 0);
    let nextDrafts = { ...drafts };
    let added = 0;
    const touchedPhases = new Set();

    for (const entry of entries) {
      const progress = summarizeRaidStrategyProgress({
        reconciliation: reconcileRaidStrategy({
          plans: [nextDrafts[planKey(entry.bossNo, entry.phaseNo)] || emptyPlan(
            bosses.find((boss) => Number(boss.bossNo) === Number(entry.bossNo)),
            entry.phaseNo,
          )],
          actualRecords: actualRecords.filter((record) => Number(record?.bossNo) === Number(entry.bossNo)
            && Number(record?.phaseNo) === Number(entry.phaseNo)),
        }),
        targetHpEok: nextDrafts[planKey(entry.bossNo, entry.phaseNo)]?.targetHpEok,
      });
      if (progress.targetReached) continue;
      // A combo built by rewriting this boss's plan was allowed to reuse the decks that plan holds,
      // so its planned attacks come out before the combo goes in — otherwise the boss doubles up.
      if (entry.replace) {
        const key = planKey(entry.bossNo, entry.phaseNo);
        const plan = nextDrafts[key];
        if (plan) {
          const cleared = clearUnmatchedPlanItems({
            plan,
            actualRecords: actualRecords.filter((record) => Number(record?.bossNo) === Number(entry.bossNo)
              && Number(record?.phaseNo) === Number(entry.phaseNo)),
            reconcile: reconcileRaidStrategy,
          });
          if (cleared.cleared) {
            nextDrafts = { ...nextDrafts, [key]: cleared.plan };
            touchedPhases.add(Number(entry.phaseNo));
          }
        }
      }
      const result = addItemsToDrafts(nextDrafts, entry.bossNo, entry.phaseNo, entry.items);
      nextDrafts = result.nextDrafts;
      added += result.added;
      if (result.added) touchedPhases.add(Number(entry.phaseNo));
    }

    if (!added) {
      const message = '추천 슬롯이 이미 배치됐거나 3타·니케 중복 제한에 걸립니다.';
      setNotice({ type: 'error', message });
      return message;
    }

    setDrafts(nextDrafts);
    const skipped = requested - added;
    setNotice({
      type: 'success',
      message: `추천 ${added}타를 계획표에 넣었습니다.${skipped > 0 ? ` ${skipped}타는 3타·니케 제한으로 제외했습니다.` : ''} 저장 버튼을 눌러야 공유됩니다.`,
    });
    return '';
  };

  // Replaces this member's planned decks with the arrangement chosen in the placement comparison.
  const applyPlacement = (allocation) => {
    if (!strategy.canManage || !allocation) return '운영진만 계획을 편집할 수 있습니다.';
    let nextDrafts = { ...drafts };
    let changed = 0;

    for (const choice of allocation.choices) {
      const [location, planIndexText] = String(choice.slotKey).split('#');
      const [bossNoText, phaseNoText] = location.split(':');
      const bossNo = numberValue(bossNoText);
      const phaseNo = numberValue(phaseNoText);
      const planIndex = numberValue(planIndexText);
      const key = planKey(bossNo, phaseNo);
      const boss = bosses.find((entry) => Number(entry.bossNo) === bossNo);
      const plan = clonePlan(nextDrafts[key] || emptyPlan(boss, phaseNo));
      const current = plan.items[planIndex];
      if (!current || textValue(current.practiceId) === textValue(choice.option.id)) continue;
      plan.items[planIndex] = {
        ...current,
        practiceId: choice.option.id,
        slotNo: numberValue(choice.option.slotNo, 1),
        title: choice.option.title,
        damageEok: numberValue(choice.option.damageEok),
        deck: Array.isArray(choice.option.deck) ? [...choice.option.deck] : [],
      };
      nextDrafts = { ...nextDrafts, [key]: plan };
      changed += 1;
    }

    if (!changed) return '바꿀 슬롯이 없습니다.';
    const issues = validateDrafts(nextDrafts).issues;
    if (issues.length) return firstStrategyIntegrityMessage({ issues });
    setDrafts(nextDrafts);
    setNotice({ type: 'success', message: `${allocation.choices[0]?.option?.nickname || '멤버'} 니케 배치를 바꿨습니다. 저장 버튼을 눌러야 공유됩니다.` });
    return '';
  };

  // Emptying slots one dialog at a time was the only way to start a phase over. Slots that a real
  // attack already matched stay: removing them would only turn those attacks into "계획 외 실전".
  const clearPhaseSlots = () => {
    if (!strategy.canManage) return;
    let cleared = 0;
    const nextDrafts = { ...drafts };
    for (const boss of bosses) {
      const key = planKey(boss.bossNo, selectedPhaseNo);
      const result = clearUnmatchedPlanItems({
        plan: clonePlan(nextDrafts[key] || emptyPlan(boss, selectedPhaseNo)),
        actualRecords: actualRecords.filter((record) => Number(record?.bossNo) === Number(boss.bossNo)
          && Number(record?.phaseNo) === Number(selectedPhaseNo)),
        reconcile: reconcileRaidStrategy,
      });
      if (!result.cleared) continue;
      cleared += result.cleared;
      nextDrafts[key] = result.plan;
    }

    setPendingClear(false);
    if (!cleared) {
      setNotice({ type: 'error', message: `${selectedPhaseNo}단에 비울 계획 슬롯이 없습니다.` });
      return;
    }
    setDrafts(nextDrafts);
    setNotice({ type: 'success', message: `${selectedPhaseNo}단 계획 슬롯 ${cleared}개를 비웠습니다. 저장 전에는 되돌리기로 복구할 수 있습니다.` });
  };

  const resetPhaseHp = () => {
    if (!strategy.canManage) return;
    setDrafts((current) => {
      const next = { ...current };
      for (const boss of bosses) {
        const key = planKey(boss.bossNo, selectedPhaseNo);
        const plan = clonePlan(next[key] || emptyPlan(boss, selectedPhaseNo));
        next[key] = { ...plan, targetHpEok: phaseResetTargetHp(boss, selectedPhaseNo) };
      }
      return next;
    });
    setNotice({ type: 'success', message: `${selectedPhaseNo}단 체력을 기본 보스 체력으로 복구했습니다.` });
  };

  const resetPhase = () => {
    setDrafts((current) => {
      const next = { ...current };
      for (const boss of bosses) {
        const key = planKey(boss.bossNo, selectedPhaseNo);
        next[key] = strategy.plansByKey.has(key) ? clonePlan(strategy.plansByKey.get(key)) : emptyPlan(boss, selectedPhaseNo);
      }
      return next;
    });
    setConflictPhases((current) => current.filter((item) => item !== selectedPhaseNo));
    setNotice({ type: '', message: '' });
  };

  // Adopt the server's copy of a phase we just saved right away, instead of waiting for the reload
  // it triggers: the merge above only replaces drafts that still match their stored plan, and the
  // server may normalize what it stores, which would otherwise leave the phase looking unsaved.
  const applySavedPlans = (savedPlans, phaseNo, sentPlans) => {
    const savedByKey = new Map((Array.isArray(savedPlans) ? savedPlans : [])
      .map((plan) => [planKey(plan.bossNo, plan.phaseNo), plan]));
    const nextDrafts = { ...draftsRef.current };
    for (const boss of bosses) {
      const key = planKey(boss.bossNo, phaseNo);
      const plan = clonePlan(savedByKey.get(key)
        || (Array.isArray(sentPlans) ? sentPlans.find((item) => Number(item.bossNo) === Number(boss.bossNo)) : null)
        || emptyPlan(boss, phaseNo));
      nextDrafts[key] = plan;
      savedSignaturesRef.current.set(key, strategyPlanSignature(plan));
    }
    draftsRef.current = nextDrafts;
    setDrafts(nextDrafts);
    setConflictPhases((current) => current.filter((item) => item !== Number(phaseNo)));
  };

  const savePhases = async (phaseNos) => {
    const targets = (Array.isArray(phaseNos) ? phaseNos : []).filter((phaseNo) => dirtyPhases.includes(phaseNo));
    if (!strategy.canManage || !targets.length || strategy.savingKey) return;
    if (strategy.migrationRequired) {
      setNotice({ type: 'error', message: '현재는 미리보기 모드입니다. 전략판 DB 적용 후 저장할 수 있습니다.' });
      return;
    }
    const integrityMessage = firstStrategyIntegrityMessage(reconciliation);
    if (integrityMessage) {
      setNotice({ type: 'error', message: integrityMessage });
      return;
    }
    const savedPhases = [];
    try {
      for (const phaseNo of targets) {
        const sentPlans = plansForSaveByPhase.get(phaseNo) || [];
        const saved = await strategy.savePhase(phaseNo, sentPlans);
        applySavedPlans(saved, phaseNo, sentPlans);
        savedPhases.push(phaseNo);
      }
      setNotice({
        type: 'success',
        message: savedPhases.length > 1
          ? `${savedPhases.join('·')}단 계획을 저장했습니다.`
          : `${savedPhases[0]}단 5보스 계획을 저장했습니다.`,
      });
    } catch (error) {
      const failed = error instanceof Error ? error.message : '전략판을 저장하지 못했습니다.';
      setNotice({
        type: 'error',
        message: savedPhases.length ? `${savedPhases.join('·')}단은 저장했지만 이후 단수에서 실패했습니다. ${failed}` : failed,
      });
    }
  };

  const savePhase = () => savePhases([selectedPhaseNo]);

  const activeDialogBoss = bosses.find((boss) => Number(boss.bossNo) === Number(dialog?.bossNo)) || null;
  const activeDialogPlan = dialog ? drafts[planKey(dialog.bossNo, selectedPhaseNo)] : null;
  const activeDialogItem = dialog ? activeDialogPlan?.items?.[dialog.planIndex] || null : null;
  const activeDialogCandidates = candidatesByBoss.get(Number(dialog?.bossNo)) || [];
  const activeRecommendationIds = new Set(recommendationItems(dialogRecommendation || {}).map((item) => item.id));

  // What putting a given damage into the open slot does to that boss, so the slot dialog can show
  // the trade before the leader applies it (the same numbers the 계산 줄 used to show after the fact).
  const previewSlotDamage = (damageEok) => {
    if (!dialog) return null;
    const progress = phaseProgressByBoss.get(Number(dialog.bossNo));
    if (!progress) return null;
    const targetHpEok = numberValue(progress.targetHpEok);
    const beforeTotalEok = numberValue(progress.projectedTotalEok);
    const currentDamageEok = numberValue(activeDialogItem?.damageEok);
    const afterTotalEok = Math.max(0, beforeTotalEok - currentDamageEok + Math.max(0, numberValue(damageEok)));
    return {
      targetHpEok,
      currentDamageEok,
      nextDamageEok: Math.max(0, numberValue(damageEok)),
      beforeTotalEok,
      afterTotalEok,
      beforeDiffEok: beforeTotalEok - targetHpEok,
      afterDiffEok: afterTotalEok - targetHpEok,
      hasCurrent: Boolean(activeDialogItem),
    };
  };
  const candidateReasonById = new Map();
  // Candidates blocked only because this member already spends that Nikke on another attack: the
  // leader cannot apply them here, but that is exactly the trade the placement comparison explains.
  const candidateComparableIds = new Set();
  const usedNikkeCodesByMember = new Map();
  if (dialog) {
    for (const candidate of activeDialogCandidates) {
      const check = assignmentIntegrityCheck(candidateToPlanItem(candidate), activeDialogItem);
      if (check.message) candidateReasonById.set(candidate.id, check.message);
      if (check.issues.some((issue) => issue.type === 'duplicate_nikke'
        && strategyMemberOpenid(issue) === textValue(candidate.memberOpenid))) candidateComparableIds.add(candidate.id);
    }

    const baseline = validateDrafts(draftsWithoutDialogItem());
    for (const attack of baseline.effectiveAttacks || []) {
      const memberOpenid = strategyMemberOpenid(attack);
      if (!memberOpenid) continue;
      const usedCodes = usedNikkeCodesByMember.get(memberOpenid) || new Set();
      for (const code of canonicalStrategyDeck(attack.deck)) usedCodes.add(code);
      usedNikkeCodesByMember.set(memberOpenid, usedCodes);
    }
  }

  if (strategy.loading) return <LoadingState label="전략판 동기화 중" variant="boss" count={5} />;
  if (strategy.errorMessage) return <section className="raid-strategy-state error"><strong>전략판을 불러오지 못했습니다.</strong><p>{strategy.errorMessage}</p><button type="button" onClick={() => strategy.refresh()}>다시 시도</button></section>;

  // Each boss column packs its own cells: remaining plan slots, one "+" cell, then the attacks that
  // already landed. Rows used to be a shared grid, so a boss whose slots were all executed showed
  // four empty rows above its results.
  const columnCellsByBoss = new Map(bosses.map((boss) => {
    const reconciled = displayReconciliationByBoss.get(Number(boss.bossNo));
    const progress = displayProgressByBoss.get(Number(boss.bossNo));
    const plans = reconciled?.unmatchedPlans || [];
    const canAdd = strategy.canManage && !progress?.targetReached && plans.length < MAX_SLOT_ROWS;
    return [Number(boss.bossNo), { plans, canAdd, actuals: practiceOnly ? [] : (reconciled?.actualEntries || []) }];
  }));
  const visibleRowCount = Math.max(MIN_SLOT_ROWS, ...[...columnCellsByBoss.values()]
    .map((column) => column.plans.length + (column.canAdd ? 1 : 0) + column.actuals.length));
  const actualRowCount = Math.max(0, ...bosses.map((boss) => phaseReconciliationByBoss.get(Number(boss.bossNo))?.actualEntries?.length || 0));
  const reviewCount = practiceOnly ? 0 : bosses.reduce((sum, boss) => (phaseProgressByBoss.get(Number(boss.bossNo))?.targetReached
    ? sum
    : sum + (phaseReconciliationByBoss.get(Number(boss.bossNo))?.actualEntries || [])
      .filter((entry) => strategyActualReviewState(entry).needsReview).length), 0);
  const hasPlanWarnings = bosses.some((boss) => phaseProgressByBoss.get(Number(boss.bossNo))?.hasWarning);
  const hasExcludedPlans = bosses.some((boss) => (phaseProgressByBoss.get(Number(boss.bossNo))?.excludedPlans?.length || 0) > 0);
  const integrityMessage = firstStrategyIntegrityMessage(reconciliation);

  return (
    <section className={`raid-strategy-board ${mobile ? 'mobile' : 'desktop'} ${strategy.canManage ? 'editable' : 'readonly'}`} aria-label="레이드 전략판">
      {strategy.migrationRequired ? <p className="raid-strategy-preview-notice" role="status"><strong>미리보기 모드</strong><span>배치와 직접 입력은 시험할 수 있지만 새로고침하면 초기화됩니다.</span></p> : null}

      <div className="raid-strategy-phase-bar">
        <div role="tablist" aria-label="전략 단수 선택">{[1, 2, 3].map((phaseNo) => <button type="button" role="tab" className={`${selectedPhaseNo === phaseNo ? 'active' : ''} ${dirtyPhases.includes(phaseNo) ? 'dirty' : ''}`.trim()} aria-selected={selectedPhaseNo === phaseNo} key={phaseNo} onClick={() => { setSelectedPhaseNo(phaseNo); setDialog(null); setPendingClear(false); setNotice({ type: '', message: '' }); }}>{phaseNo}단{dirtyPhases.includes(phaseNo) ? <i className="raid-strategy-phase-dirty" title="저장하지 않은 변경사항" aria-label="저장하지 않은 변경사항" /> : null}</button>)}</div>
        <div className="raid-strategy-phase-actions"><span>{season?.seasonNo}차 · {strategy.canManage ? '운영진 편집' : '읽기 전용'}</span>{strategy.canManage ? <button type="button" className="raid-strategy-recommend-open" onClick={() => setRecommendDialog({ bossNo: 0 })}>테트리스 추천</button> : null}{actualRowCount ? <button type="button" className={`raid-strategy-actual-toggle ${practiceOnly ? 'active' : ''}`.trim()} aria-pressed={practiceOnly} title="실전 기록을 빼고 등록된 모의전 계획만 봅니다." onClick={() => setPracticeOnly((current) => !current)}>{practiceOnly ? '실전 반영 보기' : '모의전 보기'}</button> : null}{strategy.canManage ? <button type="button" className="raid-strategy-hp-reset" onClick={resetPhaseHp}>체력 리셋</button> : null}</div>
      </div>


      <div className="raid-strategy-matrix-wrap">
        <table className="raid-strategy-matrix">
          <thead>
            <tr>
              <th className="raid-strategy-row-head">{selectedPhaseNo}단</th>
              {bosses.map((boss) => {
                const plan = drafts[planKey(boss.bossNo, selectedPhaseNo)] || emptyPlan(boss, selectedPhaseNo);
                const progress = displayProgressByBoss.get(Number(boss.bossNo));
                const total = numberValue(progress?.projectedTotalEok);
                const diff = total - numberValue(progress?.targetHpEok);
                const state = progress?.targetReached ? 'complete' : diff >= 0 ? 'enough' : 'lack';
                const bossReviewCount = practiceOnly || progress?.targetReached
                  ? 0
                  : (progress?.actualEntries || []).filter((entry) => strategyActualReviewState(entry).needsReview).length;
                return <th key={boss.bossNo}><div className="raid-strategy-boss-head">{boss.imageUrl ? <i className="raid-strategy-boss-art" style={{ backgroundImage: `url(${siteImageUrl(boss.imageUrl)})` }} aria-hidden="true" /> : null}<strong title={bossDisplayName(boss)}>{bossDisplayName(boss)}</strong><small><ElementIcon element={boss.element} /><span className="raid-strategy-boss-element">{boss.element || '-'}</span><em>실전 {progress?.actualEntries?.length || 0} · 계획 {progress?.activePlans?.length || 0}</em>{bossReviewCount ? <i className="raid-strategy-boss-review" title="실전과 다른 모의전 기록이 있습니다. 모의전을 다시 측정하거나 계획을 고쳐주세요.">모의전 {bossReviewCount}</i> : null}</small><label><span>체력</span><input type="number" min="0" step="0.01" disabled={!strategy.canManage} value={formatRaidHpInput(plan.targetHpEok)} onChange={(event) => setBossPlan(boss.bossNo, (current) => ({ ...current, targetHpEok: raidHpValueFromInput(event.target.value) }))} /><b>억</b></label><div className={`raid-strategy-boss-status ${state}`} title={`합계 ${formatEok(total)} · 목표 ${formatRaidHpEok(numberValue(progress?.targetHpEok))}`}><span>합계 {formatEok(total)}</span><strong>{progress?.targetReached ? '처치 완료' : diff >= 0 ? `초과 +${formatRaidHpEok(diff)}` : `부족 ${formatRaidHpEok(Math.abs(diff))}`}</strong>{strategy.canManage ? <button type="button" disabled={Boolean(progress?.targetReached) || !numberValue(progress?.remainingAfterActualEok)} title={numberValue(progress?.remainingAfterPlanEok) ? '' : '계획으로 채운 보스입니다. 기존 계획을 풀고 다른 조합을 볼 수 있습니다.'} onClick={() => setRecommendDialog({ bossNo: Number(boss.bossNo) })}>추천</button> : null}</div></div></th>;
              })}
            </tr>
          </thead>

          <tbody className="raid-strategy-plan-body">
            {Array.from({ length: visibleRowCount }, (_, rowIndex) => (
              <tr key={`slot-${rowIndex}`}>
                <th className="raid-strategy-row-head">슬롯 {rowIndex + 1}</th>
                {bosses.map((boss) => {
                  const column = columnCellsByBoss.get(Number(boss.bossNo)) || { plans: [], canAdd: false, actuals: [] };
                  const progress = displayProgressByBoss.get(Number(boss.bossNo));
                  const completed = Boolean(progress?.targetReached);
                  const item = column.plans[rowIndex] || null;
                  const addIndex = column.plans.length;
                  const actualIndex = rowIndex - addIndex - (column.canAdd ? 1 : 0);
                  const actual = actualIndex >= 0 ? column.actuals[actualIndex] || null : null;

                  if (actual) {
                    const review = strategyActualReviewState(actual);
                    // A real attack is the truth; when it disagrees with the plan it is the 모의전
                    // record that needs fixing, and a boss already killed needs neither.
                    const note = completed
                      ? '실전 반영 · 처치 완료'
                      : review.reason === 'unplanned'
                        ? '계획에 없던 공격'
                        : review.reason === 'damage_gap'
                          ? `모의전 ${formatEok(review.plannedEok)} 대비 ${review.gapEok > 0 ? '+' : '-'}${formatEok(Math.abs(review.gapEok))}`
                          : '계획대로 반영';
                    return <td key={boss.bossNo}><div className={`raid-strategy-actual-cell ${review.needsReview && !completed ? 'unmatched' : 'matched'}`} title={note}><div><strong title={actual.nickname}>{actual.nickname}</strong><span>실전 {actual.attackNo ? `${actual.attackNo}타` : '공격'}</span><b>{formatEok(actual.damageEok)}</b></div><RaidTetrisMiniDeck deck={actual.deck || []} compact imageLoading="eager" /><small>{note}</small></div></td>;
                  }

                  if (item) {
                    // A boss that real attacks already killed keeps its leftover plan slots visible
                    // but marked: saving the phase clears them.
                    const content = <><strong title={`${item.nickname} · ${itemTypeLabel(item)}`}>{item.nickname}</strong><span>{itemTypeLabel(item)}</span><RaidTetrisMiniDeck deck={item.deck || []} compact imageLoading="eager" /><b>{formatEok(item.damageEok)}</b>{completed ? <small>실전 처치로 제외</small> : item.note ? <small>지시 있음</small> : null}</>;
                    return <td key={boss.bossNo}><button type="button" className={`raid-strategy-cell filled ${completed ? 'obsolete' : ''}`.trim()} disabled={!strategy.canManage} onClick={() => openSlot(boss.bossNo, rowIndex)}>{content}</button></td>;
                  }

                  if (rowIndex === addIndex && column.canAdd) {
                    return <td key={boss.bossNo}><button type="button" className="raid-strategy-cell empty" onClick={() => openSlot(boss.bossNo, rowIndex)}><strong>+</strong><span>슬롯 선택</span></button></td>;
                  }

                  if (completed && rowIndex === addIndex) {
                    return <td key={boss.bossNo}><div className="raid-strategy-cell completed" aria-hidden="true"><strong>✓</strong><span>처치 완료</span></div></td>;
                  }

                  return <td key={boss.bossNo}><span className="raid-strategy-empty-actual">—</span></td>;
                })}
              </tr>
            ))}
          </tbody>

        </table>
      </div>

      <div className="raid-strategy-savebar">
        <div>{pendingClear ? <div className="raid-strategy-clear-confirm"><p className="warning" title="실전과 일치한 슬롯은 남습니다. 저장 전에는 되돌리기로 복구할 수 있습니다.">{selectedPhaseNo}단 계획 슬롯을 모두 비울까요?</p><span><button type="button" className="danger" title="실전과 일치한 슬롯은 남습니다. 저장 전에는 되돌리기로 복구할 수 있습니다." onClick={clearPhaseSlots}>비우기</button><button type="button" onClick={() => setPendingClear(false)}>취소</button></span></div> : notice.message ? <p className={notice.type}>{notice.message}</p> : integrityMessage ? <p className="error">{integrityMessage}</p> : conflictPhases.length ? <p className="warning">{conflictPhases.join('·')}단 계획이 다른 곳에서 저장됐습니다. 되돌리기로 최신 계획을 불러올 수 있습니다.</p> : reviewCount ? <p className="warning">실전과 어긋난 모의전 {reviewCount}건이 있습니다. 계획에 없던 공격이거나 모의전 딜량과 차이가 커서, 모의전 기록이나 계획을 손봐야 합니다.</p> : hasExcludedPlans ? <p className="warning">처치 완료로 제외된 계획이 있습니다. 저장하면 남은 계획 슬롯이 정리됩니다.</p> : hasPlanWarnings ? <p className="warning">실전 기록 뒤에도 남은 계획이 있습니다. 계획 슬롯을 확인해주세요.</p> : dirtyPhases.length ? <p>{dirtyPhases.join('·')}단에 저장하지 않은 변경사항이 있습니다.</p> : <p>{selectedPhaseNo}단 계획이 저장된 상태와 같습니다.</p>}</div>
        <div className="raid-strategy-save-actions">
          {/* The confirmation lives in the message area on the left: swapping buttons in and out here
              moved 되돌리기 and 저장 under the cursor mid-click. */}
          {strategy.canManage ? <button type="button" className={`secondary ${pendingClear ? 'pending' : ''}`.trim()} aria-pressed={pendingClear} onClick={() => { setPendingClear((current) => !current); setNotice({ type: '', message: '' }); }}>슬롯 비우기</button> : null}
          {strategy.canManage ? <button type="button" className="secondary" disabled={!phaseDirty || Boolean(strategy.savingKey)} onClick={resetPhase}>되돌리기</button> : null}
          {strategy.canManage && dirtyPhases.length > 1 ? <button type="button" className="secondary" disabled={Boolean(strategy.savingKey) || strategy.migrationRequired || Boolean(integrityMessage)} onClick={() => savePhases(dirtyPhases)}>변경된 {dirtyPhases.length}개 단수 저장</button> : null}
          {strategy.canManage ? <button type="button" className="primary" disabled={!phaseDirty || Boolean(strategy.savingKey) || strategy.migrationRequired || Boolean(integrityMessage)} onClick={savePhase}>{strategy.migrationRequired ? 'DB 적용 필요' : strategy.savingKey ? '저장 중…' : `${selectedPhaseNo}단 전체 저장`}</button> : null}
        </div>
      </div>

      <RaidStrategyRecommendDialog
        open={Boolean(recommendDialog)}
        mobile={mobile}
        bosses={bosses}
        phaseNo={selectedPhaseNo}
        initialBossNo={recommendDialog?.bossNo || 0}
        candidates={unassignedCandidates}
        targetsByBoss={phaseTargetsByBoss}
        planUsage={planUsage}
        effectiveAttacks={reconciliation.effectiveAttacks}
        activePlans={activeAllPlans}
        actualRecords={actualRecords}
        memberCount={members.length}
        recommendMode={recommendMode}
        onRecommendModeChange={onRecommendModeChange}
        preferAvailability={preferAvailability}
        onPreferAvailabilityChange={onPreferAvailabilityChange}
        currentAvailabilityOnly={currentAvailabilityOnly}
        onCurrentAvailabilityOnlyChange={onCurrentAvailabilityOnlyChange}
        canManage={strategy.canManage}
        onApplyCombo={(bossNo, items, options = {}) => applyRecommendation([{ bossNo, phaseNo: selectedPhaseNo, items, replace: Boolean(options.replace) }])}
        onApplyPlan={(entries) => {
          const error = applyRecommendation(entries);
          if (!error) setRecommendDialog(null);
          return error;
        }}
        onClose={() => setRecommendDialog(null)}
      />

      <RaidStrategyPlacementDialog
        open={Boolean(placementDialog)}
        mobile={mobile}
        memberOpenid={placementDialog?.memberOpenid || ''}
        bosses={bosses}
        plans={activeAllPlans}
        actualRecords={actualRecords}
        candidates={placementCandidates.length ? placementCandidates : candidates}
        focus={placementDialog?.focus || null}
        canManage={strategy.canManage}
        onApply={applyPlacement}
        onClose={() => setPlacementDialog(null)}
      />

      <RaidStrategySlotDialog
        open={Boolean(dialog && activeDialogBoss)}
        boss={activeDialogBoss}
        phaseNo={selectedPhaseNo}
        rowIndex={dialog?.displayRowIndex || 0}
        currentItem={activeDialogItem}
        candidates={activeDialogCandidates}
        members={members}
        recommendedIds={activeRecommendationIds}
        assignedLocationById={assignedLocationById}
        candidateReasonById={candidateReasonById}
        candidateComparableIds={candidateComparableIds}
        onPreviewSlotDamage={previewSlotDamage}
        syncLevelByOpenid={syncLevelByOpenid}
        onComparePlacement={(memberOpenid, optionId) => {
          if (!memberOpenid || !dialog) return;
          setPlacementDialog({
            memberOpenid,
            focus: optionId ? { slotKey: `${planKey(dialog.bossNo, selectedPhaseNo)}#${dialog.planIndex}`, optionId } : null,
          });
          setDialog(null);
        }}
        usedNikkeCodesByMember={usedNikkeCodesByMember}
        canManage={strategy.canManage}
        mobile={mobile}
        onApply={applyDialogItem}
        onDelete={deleteDialogItem}
        onClose={() => setDialog(null)}
      />
    </section>
  );
}
